import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { formatNOK } from "@/lib/format";
import {
  InvoiceTermsEditor,
  type InvoiceTerm,
} from "@/components/offer/InvoiceTermsEditor";

interface StepInvoicingProps {
  totalAmount: number;
  /**
   * VIB-686: Fleksible faktureringsvilkår. Default 60/40 oppstart/levering.
   * Erstatter den gamle invoiceModel/invoicePercent/invoiceFixed-strukturen.
   */
  invoiceTerms: InvoiceTerm[];
  onInvoiceTermsChange: (terms: InvoiceTerm[]) => void;
  paymentDueDays: number;
  onPaymentDueDaysChange: (days: number) => void;
  invoiceEmail: string;
  onInvoiceEmailChange: (email: string) => void;
  customerReferencePerson: string;
  onCustomerReferencePersonChange: (name: string) => void;
  customerReferenceNumber: string;
  onCustomerReferenceNumberChange: (num: string) => void;
  /** VIB-909: PL-styrt flagg — reise faktureres per medgått tid. */
  billsTravelPerMedgatt: boolean;
  onBillsTravelPerMedgattChange: (value: boolean) => void;
  /** VIB-943: 'hourly' (T&M) låses til 100% sluttfaktura. */
  projectType?: "fixed" | "hourly";
}

export function StepInvoicing({
  totalAmount,
  invoiceTerms,
  onInvoiceTermsChange,
  paymentDueDays,
  onPaymentDueDaysChange,
  invoiceEmail,
  onInvoiceEmailChange,
  customerReferencePerson,
  onCustomerReferencePersonChange,
  customerReferenceNumber,
  onCustomerReferenceNumberChange,
  billsTravelPerMedgatt,
  onBillsTravelPerMedgattChange,
  projectType = "fixed",
}: StepInvoicingProps) {
  const isHourly = projectType === "hourly";
  return (
    <div className="space-y-6">
      {isHourly ? (
        <div className="p-4 rounded-lg border bg-muted/30 text-sm">
          <p className="font-medium">100 % sluttfakturering</p>
          <p className="text-muted-foreground mt-1">
            Time-/T&amp;M-prosjekter faktureres etterskuddsvis per medgått tid.
            Faktureringsvilkårene er låst til én sluttfaktura (100 %).
          </p>
        </div>
      ) : (
        <InvoiceTermsEditor terms={invoiceTerms} onChange={onInvoiceTermsChange} />
      )}

      {!isHourly && totalAmount > 0 && (
        <div className="p-4 rounded-lg bg-muted/50 space-y-1">
          <p className="text-sm font-medium mb-2">Forhåndsvisning per termin</p>
          <div className="space-y-1">
            {invoiceTerms.map((term, i) => (
              <p key={i} className="text-sm flex justify-between">
                <span className="text-muted-foreground">{term.label}:</span>
                <span className="tabular-nums">
                  <strong>{formatNOK((totalAmount * term.pct) / 100)}</strong>
                  <span className="text-muted-foreground ml-2">({term.pct} %)</span>
                </span>
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="border-t pt-6 space-y-4">
        <h3 className="text-sm font-medium">Fakturerings-detaljer</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Forfallsdager</Label>
            <Input
              type="number"
              value={paymentDueDays}
              onChange={(e) => onPaymentDueDaysChange(Number(e.target.value))}
              min="1"
            />
          </div>
          <div>
            <Label>Fakturamail</Label>
            <Input
              type="email"
              value={invoiceEmail}
              onChange={(e) => onInvoiceEmailChange(e.target.value)}
              placeholder="faktura@kunde.no"
            />
          </div>
          <div>
            <Label>Deres ref.</Label>
            <Input
              value={customerReferencePerson}
              onChange={(e) => onCustomerReferencePersonChange(e.target.value)}
              placeholder="Kontaktperson hos kunde"
            />
          </div>
          <div>
            <Label>Deres ref. kode</Label>
            <Input
              value={customerReferenceNumber}
              onChange={(e) => onCustomerReferenceNumberChange(e.target.value)}
              placeholder="Kundens PO/referansenummer"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Checkbox
            checked={billsTravelPerMedgatt}
            onCheckedChange={(v) => onBillsTravelPerMedgattChange(!!v)}
            id="bills-travel-per-medgatt"
          />
          <Label htmlFor="bills-travel-per-medgatt" className="cursor-pointer">
            Reise faktureres separat (medgått)
          </Label>
        </div>
      </div>
    </div>
  );
}
